import { Reducer } from 'redux';
import { SuppressionsActionTypes, SuppressionsState } from './types';
import { omit } from 'lodash-es';
import dayjs from '@api/services/tools/dayjs';
import { ContactStatus, UnsubscribedContactsOrderBy } from '@dev/ee-api/ee-api-models';
import { DEFAULT_ACTIVE_PAGE, DEFAULT_PER_PAGE_COUNT, LONG_DATE_FORMAT } from '@api/services/constants';

const defaultState: SuppressionsState = {
    statuses: [],
    contactsLoading: false,
    contactsUploading: false,
    contacts: [],
    counts: {
        totalCount: 0,
        bouncedCount: 0,
        unsubscribedCount: 0,
        abuseCount: 0,
        inactiveCount: 0,
        staleCount: 0,
        transactionalCount: 0,
    },
    unsubscribedContacts: {
        overview: null,
        loadingUnsubscribedOverview: false,
        contactsList: [],
        pagination: {
            activePage: DEFAULT_ACTIVE_PAGE,
            email: null,
            from: null,
            perPage: DEFAULT_PER_PAGE_COUNT,
            statuses: [ContactStatus.Abuse, ContactStatus.Unsubscribed],
            to: dayjs().format(LONG_DATE_FORMAT),
            orderBy: UnsubscribedContactsOrderBy.DateDesc,
        },
        loadingUnsubscribed: false,
        statusCount: null,
        pickedContactEmail: null,
        unsubReasons: null,
    },
    activePage: 1,
    perPage: 25,
    search: null,
};

const reducer: Reducer<SuppressionsState> = (state: SuppressionsState = defaultState, action): SuppressionsState => {
    const { payload } = action;

    switch (action.type) {
        case SuppressionsActionTypes.FETCH_CONTACTS_SUCCESS:
            return { ...state, contacts: payload.contacts, contactsLoading: false, counts: payload.counts };
        case SuppressionsActionTypes.FETCH_CONTACTS_START:
            return { ...state, contactsLoading: true };
        case SuppressionsActionTypes.FETCH_CONTACTS_ERROR:
            return { ...state, contactsLoading: false };
        case SuppressionsActionTypes.CREATE_SUPPRESSIONS_START:
            return { ...state, contactsUploading: true };
        case SuppressionsActionTypes.CREATE_SUPPRESSIONS_ERROR:
            return { ...state, contactsUploading: false };
        case SuppressionsActionTypes.CREATE_SUPPRESSIONS_SUCCESS:
            return { ...state, contacts: payload.contacts, contactsUploading: false };
        case SuppressionsActionTypes.SEARCH_FOR_CONTACT:
        case SuppressionsActionTypes.FETCH_CONTACTS:
        case SuppressionsActionTypes.GET_CONTACTS_PATTERN_SUCCESS:
            return { ...state, ...payload };
        case SuppressionsActionTypes.CLEAR_CONTACTS_PATTERN:
            return omit(state, 'contactsPattern');
        case SuppressionsActionTypes.DELETE_SUPPRESSION_LIST:
            return omit(state, 'fileName');
        case SuppressionsActionTypes.CLEAR_DATA:
            return defaultState;
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW:
            return {
                ...state,
                unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribedOverview: true },
            };
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    overview: action.payload.overview,
                    pagination: action.payload.pagination,
                    loadingUnsubscribedOverview: false,
                    unsubReasons: action.payload.unsubReasons,
                },
            };
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR:
            return {
                ...state,
                unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribedOverview: false },
            };
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED:
            return { ...state, unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribed: true } };
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    contactsList: action.payload.contactsList,
                    statusCount: action.payload.statusCount,
                    pagination: action.payload.pagination,
                    loadingUnsubscribed: false,
                },
            };
        case SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR:
            return { ...state, unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribed: false } };
        case SuppressionsActionTypes.SET_PICKED_CONTACT_EMAIL:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    pickedContactEmail: action.payload.pickedContactEmail,
                },
            };
        default:
            return state;
    }
};

export { reducer as suppressions };

export default reducer;
